import { mean, median } from '../geometry/statistics.js';
import type { NormalizedRect } from '../types/geometry.js';
import { clamp, roundTo } from '../types/geometry.js';
import type { AnalysisBuffer } from '../types/image.js';
import type { FaceGeometryMetrics } from '../types/metrics.js';
import { clampRect, collectLuma, faceSkinEllipse, gridRects, lumaStats } from './regions.js';

/**
 * Face lighting analysis: left/right balance, vertical falloff and *local*
 * shadows on the skin.
 *
 * Cross-side balance and local shadows are deliberately different questions.
 * A face lit evenly but with a single hard spot under the nose shows a local
 * shadow and almost no left/right difference; a face lit from one side shows
 * both. The two checks consume the same measurement but judge it differently.
 */

export interface LightingBalanceAnalysis {
  leftFaceLuma: number;
  rightFaceLuma: number;
  /** |left - right| in luma units. */
  difference: number;
  /** The same difference as a percentage of the mean face luma. */
  differencePercent: number;
  /** Signed: top third minus bottom third (positive = forehead brighter). */
  verticalGradient: number;
  /** Signed: right half minus left half (positive = right half brighter). */
  horizontalGradient: number;
  topFaceLuma: number;
  bottomFaceLuma: number;
  darkerSide: 'left' | 'right';
  leftSamples: number;
  rightSamples: number;
  innerRect: NormalizedRect;
  leftRect: NormalizedRect;
  rightRect: NormalizedRect;
}

const FACE_INSET_X = 0.14;
const FACE_INSET_Y = 0.22;
const FACE_INNER_WIDTH = 0.72;
const FACE_INNER_HEIGHT = 0.56;

const emptyBalance = (): LightingBalanceAnalysis => ({
  leftFaceLuma: 0,
  rightFaceLuma: 0,
  difference: 0,
  differencePercent: 0,
  verticalGradient: 0,
  horizontalGradient: 0,
  topFaceLuma: 0,
  bottomFaceLuma: 0,
  darkerSide: 'left',
  leftSamples: 0,
  rightSamples: 0,
  innerRect: { x: 0, y: 0, width: 0, height: 0 },
  leftRect: { x: 0, y: 0, width: 0, height: 0 },
  rightRect: { x: 0, y: 0, width: 0, height: 0 },
});

/**
 * Measures the inner face box split at `geometry.faceCenterX`.
 *
 * The split uses the *measured* face centre rather than the bounding-box middle,
 * so a slightly asymmetric detector box does not look like uneven lighting.
 */
export const analyzeLightingBalance = (input: {
  buffer: AnalysisBuffer;
  geometry: FaceGeometryMetrics;
}): LightingBalanceAnalysis => {
  const { buffer, geometry } = input;
  if (buffer.width <= 0 || buffer.height <= 0) return emptyBalance();

  const face = geometry.faceBox;
  const inner = clampRect({
    x: face.x + face.width * FACE_INSET_X,
    y: face.y + face.height * FACE_INSET_Y,
    width: face.width * FACE_INNER_WIDTH,
    height: face.height * FACE_INNER_HEIGHT,
  });
  if (inner.width <= 0 || inner.height <= 0) return emptyBalance();

  const splitX = clamp(
    geometry.faceCenterX,
    inner.x + inner.width * 0.15,
    inner.x + inner.width * 0.85,
  );
  const leftRect = clampRect({
    x: inner.x,
    y: inner.y,
    width: Math.max(0.005, splitX - inner.x),
    height: inner.height,
  });
  const rightRect = clampRect({
    x: splitX,
    y: inner.y,
    width: Math.max(0.005, inner.x + inner.width - splitX),
    height: inner.height,
  });
  const topRect = clampRect({
    x: inner.x,
    y: inner.y,
    width: inner.width,
    height: inner.height / 3,
  });
  const bottomRect = clampRect({
    x: inner.x,
    y: inner.y + (inner.height * 2) / 3,
    width: inner.width,
    height: inner.height / 3,
  });

  const left = lumaStats(collectLuma(buffer.luma, buffer.width, buffer.height, leftRect));
  const right = lumaStats(collectLuma(buffer.luma, buffer.width, buffer.height, rightRect));
  const top = lumaStats(collectLuma(buffer.luma, buffer.width, buffer.height, topRect));
  const bottom = lumaStats(collectLuma(buffer.luma, buffer.width, buffer.height, bottomRect));

  const difference = Math.abs(left.mean - right.mean);
  const average = (left.mean + right.mean) / 2;

  return {
    leftFaceLuma: left.mean,
    rightFaceLuma: right.mean,
    difference,
    differencePercent: average > 1 ? (difference / average) * 100 : 0,
    verticalGradient: top.mean - bottom.mean,
    horizontalGradient: right.mean - left.mean,
    topFaceLuma: top.mean,
    bottomFaceLuma: bottom.mean,
    darkerSide: left.mean < right.mean ? 'left' : 'right',
    leftSamples: left.count,
    rightSamples: right.count,
    innerRect: inner,
    leftRect,
    rightRect,
  };
};

export interface FaceShadowCell {
  id: string;
  rect: NormalizedRect;
  meanLuma: number;
  /** How much darker than the face median this cell is. */
  delta: number;
}

export interface FaceShadowAnalysis {
  faceMedianLuma: number;
  /** Share of valid face cells that are shadowed. */
  shadowAreaRatio: number;
  sideDifference: number;
  /** Largest luma jump between horizontally adjacent cells (hard boundary). */
  edgeStrength: number;
  leftMean: number;
  rightMean: number;
  regions: FaceShadowCell[];
  sampleCells: number;
}

const EMPTY_SHADOW: FaceShadowAnalysis = {
  faceMedianLuma: 0,
  shadowAreaRatio: 0,
  sideDifference: 0,
  edgeStrength: 0,
  leftMean: 0,
  rightMean: 0,
  regions: [],
  sampleCells: 0,
};

const MIN_CELL_SAMPLES = 12;

/**
 * Local-shadow analysis over a coarse grid clipped to the face ellipse.
 *
 * A cell counts as shadowed when it is `shadowDelta` luma darker than the face
 * *median*: the median is robust to the shadow itself, unlike the face mean.
 * `edgeStrength` separates a soft gradient from a hard shadow boundary.
 */
export const analyzeFaceShadow = (input: {
  buffer: AnalysisBuffer;
  geometry: FaceGeometryMetrics;
  shadowDelta?: number;
  columns?: number;
  rows?: number;
}): FaceShadowAnalysis => {
  const { buffer, geometry } = input;
  if (buffer.width <= 0 || buffer.height <= 0) return EMPTY_SHADOW;

  const shadowDelta = input.shadowDelta ?? 22;
  const columns = clamp(Math.round(input.columns ?? 6), 3, 12);
  const rows = clamp(Math.round(input.rows ?? 8), 3, 16);
  /** Skin only: the face oval, so hair above the hairline is never sampled. */
  const skin = faceSkinEllipse(geometry);
  const inset = 0.05;
  const inner = clampRect({
    x: skin.x + skin.width * inset,
    y: skin.y + skin.height * inset,
    width: skin.width * (1 - inset * 2),
    height: skin.height * (1 - inset * 2),
  });
  if (inner.width <= 0 || inner.height <= 0) return EMPTY_SHADOW;

  const grid = gridRects(inner, columns, rows);
  const cellMeans = new Map<string, number>();
  const cells: Array<{ key: string; rect: NormalizedRect; mean: number; left: boolean }> = [];

  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < columns; col += 1) {
      const rect = grid[row * columns + col] as NormalizedRect;
      const values = collectLuma(buffer.luma, buffer.width, buffer.height, rect, {
        clipEllipse: skin,
      });
      if (values.length < MIN_CELL_SAMPLES) continue;
      const stats = lumaStats(values);
      const key = `${row}:${col}`;
      cellMeans.set(key, stats.mean);
      cells.push({
        key,
        rect,
        mean: stats.mean,
        left: rect.x + rect.width / 2 < geometry.faceCenterX,
      });
    }
  }

  if (cells.length < 6) return EMPTY_SHADOW;

  const faceMedianLuma = median(cells.map((cell) => cell.mean));
  const threshold = faceMedianLuma - shadowDelta;
  const leftCells = cells.filter((cell) => cell.left);
  const rightCells = cells.filter((cell) => !cell.left);
  const leftMean = mean(leftCells.map((cell) => cell.mean));
  const rightMean = mean(rightCells.map((cell) => cell.mean));

  let edgeStrength = 0;
  for (let row = 0; row < rows; row += 1) {
    let previous: number | null = null;
    for (let col = 0; col < columns; col += 1) {
      const value = cellMeans.get(`${row}:${col}`);
      if (value === undefined) {
        previous = null;
        continue;
      }
      if (previous !== null) edgeStrength = Math.max(edgeStrength, Math.abs(value - previous));
      previous = value;
    }
  }

  const shadowed = cells.filter((cell) => cell.mean < threshold);
  const regions: FaceShadowCell[] = shadowed.map((cell, index) => ({
    id: `shadow_face_${index}`,
    rect: cell.rect,
    meanLuma: roundTo(cell.mean, 1),
    delta: roundTo(faceMedianLuma - cell.mean, 1),
  }));

  return {
    faceMedianLuma,
    shadowAreaRatio: cells.length > 0 ? shadowed.length / cells.length : 0,
    sideDifference: Math.abs(leftMean - rightMean),
    edgeStrength,
    leftMean,
    rightMean,
    regions,
    sampleCells: cells.length,
  };
};
